import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0:00";
  const minutes = Math.floor(seconds / 60);
  const remaining = Math.floor(seconds % 60);
  return `${minutes}:${String(remaining).padStart(2, "0")}`;
};

export default function TrackCard({
  title,
  year,
  instrumentation,
  detail,
  sources,
  mediaType = "audio",
  score,
  onOpen,
  activeAudioId,
  onActivateAudio,
  process,
}) {
  const isVideo = mediaType === "video";
  const mediaRef = useRef(null);
  const metaVideoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const cardId = title;

  useEffect(() => {
    if (isVideo || !mediaRef.current) return;
    if (activeAudioId !== cardId) {
      mediaRef.current.pause();
      mediaRef.current.currentTime = 0;
      setIsPlaying(false);
      setProgress(0);
    }
  }, [activeAudioId, cardId, isVideo]);

  const togglePlay = (e) => {
    e?.stopPropagation();
    const media = mediaRef.current;
    if (!media) return;
    if (media.paused) {
      onActivateAudio?.(cardId);
      media.play().catch(() => {});
    } else {
      media.pause();
    }
  };

  const toggleExpand = () => setExpanded((x) => !x);

  const isActive = !isVideo && activeAudioId === cardId && isPlaying;
  const desc = instrumentation || detail;

  return (
    <article className="group border-b border-[var(--rule)] last:border-b-0">
      <div
        className="flex items-start gap-4 py-6 cursor-pointer"
        onClick={isVideo ? () => onOpen?.() : togglePlay}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (isVideo) onOpen?.();
            else togglePlay(e);
          }}
          className={`flex-shrink-0 grid place-items-center h-[36px] w-[36px] rounded-full border transition-all duration-200 mt-[2px] ${
            isActive
              ? "bg-[var(--text)] border-[var(--text)] text-white"
              : "border-[var(--rule)] text-[var(--muted)] hover:border-[var(--text)] hover:text-[var(--text)]"
          }`}
          aria-label={isPlaying ? `Pause ${title}` : isVideo ? `Watch ${title}` : `Play ${title}`}
        >
          {isPlaying && !isVideo ? (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" className="translate-x-[1px]">
              <path d="M8 5v14l11-7-11-7z" />
            </svg>
          )}
        </button>

        <div className="flex-1 min-w-0" onClick={(e) => { e.stopPropagation(); toggleExpand(); }}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="font-sans text-[16px] font-medium tracking-[-0.01em] text-[var(--text)]">
              {title}
            </span>
            <div className="flex items-baseline gap-3 flex-shrink-0">
              {score && (
                <a
                  href={score}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="font-sans text-[11px] text-[var(--faint)] hover:text-[var(--text)] transition-colors underline underline-offset-2 decoration-[var(--rule)]"
                >
                  Score
                </a>
              )}
              {isVideo && (
                <span className="font-sans text-[11px] text-[var(--faint)]">
                  Video
                </span>
              )}
              <span className="font-mono text-[11px] text-[var(--faint)] tabular-nums">
                {isVideo ? formatTime(videoDuration) : formatTime(duration)}
              </span>
            </div>
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            {desc && (
              <span className="font-sans text-[12px] text-[var(--muted)]">{desc}</span>
            )}
            {desc && year && <span className="font-sans text-[12px] text-[var(--faint)]">·</span>}
            {year && (
              <span className="font-mono text-[11px] text-[var(--faint)] tabular-nums">{year}</span>
            )}
          </div>
        </div>
      </div>

      {!isVideo && isActive && (
        <div className="flex items-center gap-3 pb-5 pl-[52px]">
          <div className="flex-1">
            <input
              type="range"
              min="0"
              max={duration || 0}
              step="0.01"
              value={progress}
              onChange={(e) => {
                const t = Number(e.target.value);
                if (!mediaRef.current || Number.isNaN(t)) return;
                mediaRef.current.currentTime = t;
                setProgress(t);
              }}
              className="track-range"
            />
          </div>
          <span className="font-mono text-[11px] text-[var(--faint)] tabular-nums flex-shrink-0">
            {formatTime(progress)}
          </span>
        </div>
      )}

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="process"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
            className="overflow-hidden"
          >
            <div className="pl-[52px] pb-5">
              {process || (
                <p className="font-sans text-[13px] leading-[1.7] text-[var(--muted)] italic">
                  Process notes coming soon.
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!isVideo && (
        <audio
          ref={mediaRef}
          preload="metadata"
          onTimeUpdate={() => mediaRef.current && setProgress(mediaRef.current.currentTime)}
          onLoadedMetadata={() => mediaRef.current && setDuration(mediaRef.current.duration || 0)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
        >
          {(sources || []).map((s) => <source key={s.src} src={s.src} type={s.type} />)}
        </audio>
      )}

      {isVideo && (
        <video
          ref={metaVideoRef}
          preload="metadata"
          muted
          playsInline
          onLoadedMetadata={() =>
            metaVideoRef.current && setVideoDuration(metaVideoRef.current.duration || 0)
          }
          style={{ display: "none" }}
        >
          {(sources || []).map((s) => <source key={s.src} src={s.src} type={s.type} />)}
        </video>
      )}
    </article>
  );
}
